import { UIsRequestMessageSchema, Message, CollectionRegistry } from '../types';
import { logger } from '../utils/logger';

/**
 * Handle unified UIs management request
 * Supports operations: create, update, delete, getAll, getOne, query
 * Only accepts requests from 'admin' type
 *
 * Uses database collections only (no file-based fallback)
 */
export async function handleUIsRequest(
  data: any,
  collections: CollectionRegistry,
  sendMessage: (message: Message) => void
): Promise<void> {
  // Helper function to execute collection operations
  const executeCollection = async (collection: string, op: string, params: any): Promise<any> => {
    const handler = collections[collection]?.[op];
    if (!handler) {
      throw new Error(`Collection handler not found: ${collection}.${op}`);
    }
    return await handler(params);
  };

  try {
    const request = UIsRequestMessageSchema.parse(data);
    const { id, payload, from } = request;
    const { operation, data: requestData } = payload;
    const uiId = requestData?.uiId;
    const ui = requestData?.ui;
    const projectId = requestData?.projectId;
    const name = requestData?.name;
    const description = requestData?.description;
    const createdBy = requestData?.createdBy;
    const updatedBy = requestData?.updatedBy;
    const numericId = requestData?.id;
    // Query operation fields
    const filters = requestData?.filters;
    const limit = requestData?.limit;
    const sort = requestData?.sort;

    // Verify request is from admin
    if (from.type !== 'admin') {
      sendResponse(id, {
        success: false,
        error: 'Unauthorized: Only admin can manage UIs'
      }, sendMessage, from.id);
      logger.warn(`Unauthorized UI management attempt from: ${from.type}`);
      return;
    }

    // Route to appropriate operation handler
    switch (operation) {
      case 'create':
        await handleCreate(id, uiId, ui, projectId, name, description, createdBy, executeCollection, sendMessage, from.id);
        break;

      case 'update':
        await handleUpdate(id, numericId, ui, name, description, updatedBy, executeCollection, sendMessage, from.id);
        break;

      case 'delete':
        await handleDelete(id, numericId, executeCollection, sendMessage, from.id);
        break;

      case 'getAll':
        await handleGetAll(id, executeCollection, sendMessage, from.id);
        break;

      case 'getOne':
        await handleGetOne(id, numericId, executeCollection, sendMessage, from.id);
        break;

      case 'query':
        await handleQuery(id, filters, limit, sort, executeCollection, sendMessage, from.id);
        break;

      default:
        sendResponse(id, {
          success: false,
          error: `Unknown operation: ${operation}`
        }, sendMessage, from.id);
    }

  } catch (error) {
    logger.error('Failed to handle UIs request:', error);
    sendResponse(null, {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }, sendMessage);
  }
}

/**
 * Handle create UI operation
 */
async function handleCreate(
  id: string,
  uiId: string | undefined,
  ui: any,
  projectId: string | undefined,
  name: string | undefined,
  description: string | undefined,
  createdBy: string | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Validate input
  if (!uiId || uiId.trim().length === 0) {
    sendResponse(id, {
      success: false,
      error: 'UI ID is required and cannot be empty'
    }, sendMessage, clientId);
    return;
  }

  if (!projectId || projectId.trim().length === 0) {
    sendResponse(id, {
      success: false,
      error: 'Project ID is required and cannot be empty'
    }, sendMessage, clientId);
    return;
  }

  try {
    const result = await executeCollection('uis', 'create', {
      uiId,
      projectId,
      name: name || uiId,
      description,
      ui,
      createdBy
    });

    if (result && result.success) {
      logger.info(`UI created successfully, ID: ${result.data?.id}`);
      sendResponse(id, {
        success: true,
        data: {
          id: result.data?.id,
          uiId: result.data?.uiId || uiId,
          ui: result.data?.ui || ui,
          message: `UI created successfully`
        }
      }, sendMessage, clientId);
    } else {
      sendResponse(id, {
        success: false,
        error: 'Failed to create UI'
      }, sendMessage, clientId);
    }
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create UI'
    }, sendMessage, clientId);
  }
}

/**
 * Handle update UI operation
 */
async function handleUpdate(
  id: string,
  numericId: number | undefined,
  ui: any,
  name: string | undefined,
  description: string | undefined,
  updatedBy: string | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Validate input
  if (!numericId) {
    sendResponse(id, {
      success: false,
      error: 'UI ID is required'
    }, sendMessage, clientId);
    return;
  }

  try {
    const result = await executeCollection('uis', 'update', {
      id: numericId,
      name,
      description,
      ui,
      updatedBy
    });

    if (result && result.success) {
      logger.info(`UI updated successfully, ID: ${numericId}`);
      sendResponse(id, {
        success: true,
        data: {
          id: numericId,
          uiId: result.data?.uiId,
          ui: result.data?.ui || ui,
          message: `UI updated successfully`
        }
      }, sendMessage, clientId);
    } else {
      sendResponse(id, {
        success: false,
        error: 'Failed to update UI'
      }, sendMessage, clientId);
    }
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update UI'
    }, sendMessage, clientId);
  }
}

/**
 * Handle delete UI operation
 */
async function handleDelete(
  id: string,
  numericId: number | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Validate input
  if (!numericId) {
    sendResponse(id, {
      success: false,
      error: 'UI ID is required'
    }, sendMessage, clientId);
    return;
  }

  try {
    const result = await executeCollection('uis', 'delete', { id: numericId });

    if (result && result.success) {
      logger.info(`UI deleted successfully, ID: ${numericId}`);
      sendResponse(id, {
        success: true,
        data: {
          id: numericId,
          message: `UI deleted successfully`
        }
      }, sendMessage, clientId);
    } else {
      sendResponse(id, {
        success: false,
        error: 'Failed to delete UI'
      }, sendMessage, clientId);
    }
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete UI'
    }, sendMessage, clientId);
  }
}

/**
 * Handle get all UIs operation
 */
async function handleGetAll(
  id: string,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  try {
    const result = await executeCollection('uis', 'getAll', {});

    if (result && result.success) {
      logger.info(`Retrieved ${result.count} UIs`);
      sendResponse(id, {
        success: true,
        data: {
          uis: result.data,
          count: result.count,
          message: `Retrieved ${result.count} UIs`
        }
      }, sendMessage, clientId);
    } else {
      sendResponse(id, {
        success: false,
        error: 'Failed to get UIs'
      }, sendMessage, clientId);
    }
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get UIs'
    }, sendMessage, clientId);
  }
}

/**
 * Handle get one UI operation
 */
async function handleGetOne(
  id: string,
  numericId: number | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Validate input
  if (!numericId) {
    sendResponse(id, {
      success: false,
      error: 'UI ID is required'
    }, sendMessage, clientId);
    return;
  }

  try {
    const result = await executeCollection('uis', 'getOne', { id: numericId });

    if (result && result.success) {
      logger.info(`Retrieved UI ID: ${numericId}`);
      sendResponse(id, {
        success: true,
        data: {
          id: numericId,
          uiId: result.data?.uiId,
          ui: result.data?.ui || result.data,
          message: `Retrieved UI`
        }
      }, sendMessage, clientId);
    } else {
      sendResponse(id, {
        success: false,
        error: `UI not found`
      }, sendMessage, clientId);
    }
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get UI'
    }, sendMessage, clientId);
  }
}

/**
 * Handle query UIs operation with filters
 */
async function handleQuery(
  id: string,
  filters: any,
  limit: number | undefined,
  sort: string | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  try {
    const result = await executeCollection('uis', 'query', {
      filters: filters || {},
      limit,
      sort
    });

    if (result && result.success) {
      logger.info(`Query returned ${result.count} UIs`);
      sendResponse(id, {
        success: true,
        data: {
          uis: result.data,
          count: result.count,
          message: `Query returned ${result.count} UIs`
        }
      }, sendMessage, clientId);
    } else {
      sendResponse(id, {
        success: false,
        error: 'Failed to query UIs'
      }, sendMessage, clientId);
    }
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to query UIs'
    }, sendMessage, clientId);
  }
}

/**
 * Send UIs response
 */
function sendResponse(
  id: string | null,
  res: { success: boolean; error?: string; data?: any },
  sendMessage: (message: Message) => void,
  clientId?: string,
): void {
  const response: Message = {
    id: id || 'unknown',
    type: 'UIS_RES',
    from: { type: 'data-agent' },
    to: {
      type: 'admin',
      id: clientId
    },
    payload: {
      ...res,
    }
  };

  sendMessage(response);
}
